import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { Input } from 'common-src/components/base';
import { apiRequest, getRestErrorMessage, HttpMethods } from 'common-src/features/rest';

import usePopup from 'src/hooks/usePopup';
import BasePopup from 'src/popups/BasePopup';

import styles from './AddPayorPopup.module.scss';
import { catchErrors, getIsButtonEnabled, normalizePayorId, validatePayor } from './helpers';

const initialState = { id: '', payorName: '' };

const AddPayorPopup = ({ onClose }) => {
  const [data, setData] = useState(initialState);

  const { errors, setErrors, renderButtons } = usePopup();

  const onSubmitHandler = async (loadingCallback, successCallback, errorCallback) => {
    const { errors, hasErrors } = catchErrors(data);
    if (hasErrors) {
      setErrors(errors);
      return;
    }

    loadingCallback();
    try {
      const response = await apiRequest({
        endpoint: 'payorDetails',
        method: HttpMethods.Post,
        body: { id: normalizePayorId(data.id), payorName: data.payorName.trim() },
      });
      const body = await response.json();
      if (!response.ok) {
        errorCallback(
          response.status === 403
            ? 'Action not permitted for user role'
            : getRestErrorMessage(body) || `Request failed with status ${response.status}`,
        );
        return;
      }
      successCallback('Payor created!');
      onClose(true);
    } catch (err) {
      errorCallback(err.message);
    }
  };

  const onChangeHandler = (field, value) => {
    setErrors((prev) => ({ ...prev, ...validatePayor(field, value) }));
    setData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <BasePopup id="add-payor" open onClose={onClose} title="Add Payor">
      <div className={[styles.grid, 'gap-16'].join(' ')}>
        <Input
          id="payor-id"
          placeholder="e.g. SEIU"
          label="Payor ID"
          required
          value={data.id}
          errorText={errors.id}
          onTextChange={(value) => onChangeHandler('id', value.toUpperCase())}
        />
        <Input
          id="payor-name"
          placeholder="Enter payor name"
          label="Payor Name"
          required
          value={data.payorName}
          errorText={errors.payorName}
          onTextChange={(value) => onChangeHandler('payorName', value)}
        />
      </div>
      {renderButtons({
        containerClassName: styles.buttonsContainer,
        onClose,
        onSubmit: onSubmitHandler,
        isSubmitEnabled: getIsButtonEnabled(data, errors),
        submitButtonText: 'Save and close',
      })}
    </BasePopup>
  );
};

AddPayorPopup.propTypes = {
  onClose: PropTypes.func,
};

export default AddPayorPopup;
